import { useState, useEffect } from "react";
import { MapPin, Clock, ChevronDown, Bus, ArrowRight, Package, X } from "lucide-react";
import { siteConfig } from "@/config/siteConfig";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SkeletonCard, SkeletonGrid } from "@/components/ui/skeleton-card";

export function ServicesSection() {
  const [isLoading, setIsLoading] = useState(true);
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const [stopsModalOpen, setStopsModalOpen] = useState<number | null>(null);

  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleExpand = (id: number) => {
    if (expandedService === id) {
      setExpandedService(null);
    } else {
      setExpandedService(id);
    }
  };

  const handleBookRoute = () => {
    window.open(siteConfig.bookingUrl, '_blank');
  };

  const handleBookService = () => {
    window.open(siteConfig.bookingUrl, '_blank');
  };

  if (isLoading) {
    return (
      <section id="services" className="section-padding relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-16 animate-fade-in-up">
            <div className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <div className="h-4 w-20 bg-primary/20 rounded-full animate-pulse" />
            </div>
            <div className="h-12 w-80 bg-muted rounded-lg animate-pulse mx-auto mb-6" />
            <div className="h-6 w-96 bg-muted rounded animate-pulse mx-auto" />
          </div>

          {/* Routes Grid */}
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12">
            {Array.from({ length: 2 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>

          {/* Luggage Services Grid */}
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {Array.from({ length: 2 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="section-padding relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 animate-fade-in-up">
          <div className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            Our Services
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Travel & <span className="text-gradient">Services</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Cross-border travel and professional luggage services. Book your seat and send your luggage with confidence.
          </p>
        </div>

        {/* Routes Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12">
          {siteConfig.routes.map((route, index) => (
            <div
              key={route.id}
              className="bg-card/60 backdrop-blur-xl border border-border/50 rounded-2xl transition-all duration-500 animate-fade-in-up relative overflow-hidden group"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Subtle bus image accent in card background - much more subtle */}
              {siteConfig.additionalBusImages.enabled && siteConfig.additionalBusImages.images.length > 0 && (
                <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none">
                  <img 
                    src={siteConfig.additionalBusImages.images[0]?.path}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}
              
              {/* Route Header */}
              <div className="p-6 border-b border-border/50 relative z-20">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:neon-glow transition-all duration-300">
                      <Bus className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-lg text-foreground">
                        {route.from} → {route.to}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Dep: {route.departure}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          Arr: {route.arrival}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Trigger for Stops */}
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      className="flex items-center gap-2 text-primary text-sm font-medium hover:underline cursor-pointer"
                      type="button"
                      onClick={() => setStopsModalOpen(route.id)}
                    >
                      View pickup & dropoff details
                      <MapPin className="w-4 h-4" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-semibold">
                        Route: {route.from} → {route.to}
                      </DialogTitle>
                      <DialogDescription className="text-muted-foreground">
                        All pickup and dropoff locations with times
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="max-h-[60vh] overflow-y-auto pr-4">
                      <div className="space-y-6">
                        {/* Pickup Points */}
                        <div>
                          <h3 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                            <Bus className="w-5 h-5 text-primary" />
                            Pickup Points - {route.from}
                          </h3>
                          <div className="space-y-3">
                            {route.pickupPoints?.map((pickup, i) => (
                              <div key={i} className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                                <div className="font-semibold text-foreground mb-2">{pickup.name}</div>
                                <div className="space-y-2 text-sm text-muted-foreground">
                                  <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                                    <div>
                                      <div className="font-medium text-foreground">{pickup.address}</div>
                                      {pickup.landmark && <div className="text-xs">{pickup.landmark}</div>}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4 ml-6 text-sm">
                                    <span><strong>Check-in:</strong> {pickup.checkIn}</span>
                                    <span><strong>Depart:</strong> {pickup.departure}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Dropoff Points */}
                        <div>
                          <h3 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-secondary" />
                            Arrival Stops - {route.to}
                          </h3>
                          <div className="space-y-3">
                            {route.dropoffPoints?.map((dropoff, i) => (
                              <div key={i} className="p-4 rounded-lg bg-secondary/10 border border-secondary/30">
                                <div className="font-semibold text-foreground mb-2">{dropoff.name}</div>
                                <div className="space-y-2 text-sm text-muted-foreground">
                                  <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" />
                                    <div>
                                      <div className="font-medium text-foreground">{dropoff.address}</div>
                                      {dropoff.landmark && <div className="text-xs">{dropoff.landmark}</div>}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 ml-6 text-sm">
                                    <Clock className="w-4 h-4" />
                                    <span><strong>Arrival:</strong> {dropoff.arrival}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Book Button */}
              <div className="p-6 pt-4 relative z-20">
                {siteConfig.bookingEnabled ? (
                  <Button
                    onClick={handleBookRoute}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold group"
                  >
                    Book This Route
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                ) : (
                  <Button
                    disabled
                    className="w-full bg-muted text-muted-foreground cursor-not-allowed opacity-60 pointer-events-none font-semibold"
                  >
                    Booking Coming Soon
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Luggage Services Grid */}
        {siteConfig.luggageServicesEnabled && (
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {siteConfig.luggageServices.map((service, index) => (
            <div
              key={service.id}
              className="bg-card/60 backdrop-blur-xl border border-border/50 rounded-2xl transition-all duration-500 animate-fade-in-up relative overflow-hidden group"
              style={{ animationDelay: `${(siteConfig.routes.length + index) * 150}ms` }}
            >
              {/* Subtle bus image accent in card background - much more subtle */}
              {siteConfig.additionalBusImages.enabled && siteConfig.additionalBusImages.images.length > 0 && (
                <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none">
                  <img 
                    src={siteConfig.additionalBusImages.images[1]?.path}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}
              
              {/* Service Header */}
              <div className="p-6 border-b border-border/50 relative z-20">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:neon-glow transition-all duration-300">
                      <Package className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-lg text-foreground">
                        {service.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Toggle Button */}
                <button
                  onClick={() => handleExpand(service.id)}
                  className="flex items-center gap-2 text-primary text-sm font-medium hover:underline cursor-pointer"
                  type="button"
                >
                  View details
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${
                      expandedService === service.id ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {/* Expandable Features */}
              <div 
                className={`overflow-hidden transition-all duration-300 relative z-20 ${
                  expandedService === service.id ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-6 bg-secondary/30">
                  <h4 className="text-sm font-medium text-muted-foreground mb-4">Service Features:</h4>
                  <div className="space-y-3">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                        <span className="text-sm text-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Book Button */}
              <div className="p-6 pt-4 relative z-20">
                {siteConfig.bookingEnabled ? (
                  <Button
                    onClick={handleBookService}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold group"
                  >
                    Book This Service
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                ) : (
                  <Button
                    disabled
                    className="w-full bg-muted text-muted-foreground cursor-not-allowed opacity-60 pointer-events-none font-semibold"
                  >
                    Booking Coming Soon
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Booking Info */}
        <div className="mt-16 text-center animate-fade-in-up animation-delay-400">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 glass rounded-2xl p-6 neon-border">
            <div className="text-left">
              <h4 className="font-display font-semibold text-lg mb-1">
                Ready to Book Your Journey?
              </h4>
              <p className="text-muted-foreground text-sm">
                {siteConfig.hasApp 
                  ? 'Contact us today or book directly through our app'
                  : 'Contact us today to book your travel or luggage services'
                }
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => window.open(`tel:${siteConfig.phone[0].replace(/\s/g, '')}`, '_self')}
                variant="outline"
                className="border-primary/50 hover:bg-primary/10 whitespace-nowrap"
              >
                Call Now
              </Button>
              {siteConfig.hasApp && (
                <Button
                  onClick={() => window.open(siteConfig.appStoreUrl, '_blank')}
                  variant="outline"
                  className="border-primary/50 hover:bg-primary/10 whitespace-nowrap"
                >
                  Download App
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
